import axios from '../axios';
import * as countries from '../countries';
import * as models from '../models';
import * as stax from '../stax';
import { UserDetails } from './users';

declare interface getPaymentsAPIResp {
    paymentOptions: models.PaymentOption[];
}

export async function GetPaymentOptions(params?: Record<string, unknown>): Promise<models.PaymentOption[]> {
    return axios.get<getPaymentsAPIResp>('/payment-options', { params }).then(resp => resp.data.paymentOptions);
}

declare interface newPaymentOptionOpts {
    user: UserDetails;

    paymentOption: models.PaymentOption;

    creditCardNumber?: string;
    expireMonth?: number;
    expireYear?: number;
    securityCode?: string;

    bankAccount?: string;
    bankRouting?: string;
    accountType?: 'checking' | 'savings';
    bankType?: 'business' | 'personal';

    fattmerchantOmniWebPaymentsToken?: string;
}

export async function NewPaymentOption(opts: newPaymentOptionOpts): Promise<models.PaymentOption> {
    opts.user.tz ??= Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!opts.paymentOption.country && opts.paymentOption.countryID) {
        opts.paymentOption.country = { id: opts.paymentOption.countryID };
    }
    if (!opts.paymentOption.state && opts.paymentOption.stateID) {
        opts.paymentOption.state = { id: opts.paymentOption.stateID };
    }

    if (!opts.paymentOption.country?.countryCode3 && opts.paymentOption.country?.id) {
        opts.paymentOption.country = await countries.GetCountry(opts.paymentOption.country?.id);
    }

    if (opts.paymentOption.country?.id && !opts.paymentOption.state?.stateCode && opts.paymentOption.state?.id) {
        opts.paymentOption.state = await countries.GetState(opts.paymentOption.country?.id, opts.paymentOption.state.id);
    }

    let fattmerchantOmniPaymentMethodID: string | undefined;
    if (opts.paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
        const details = {
            addressLine1: opts.paymentOption.addressLine1 || '',
            addressLine2: opts.paymentOption.addressLine2 || '',
            city: opts.paymentOption.city || '',
            countryCode3: opts.paymentOption.country?.countryCode3 || '',
            stateCode: opts.paymentOption.state?.stateCode || '',
            card: opts.creditCardNumber && opts.expireMonth && opts.expireYear && opts.securityCode ? {
                creditCardNumber: opts.creditCardNumber.replace(/\D+/g, ''),
                expireMonth: opts.expireMonth,
                expireYear: opts.expireYear,
                securityCode: opts.securityCode,
            } : undefined,
            fullName: opts.paymentOption.fullName || '',
            phone: opts.paymentOption.phone || '',
            webPaymentsToken: opts.fattmerchantOmniWebPaymentsToken || '',
            zip: opts.paymentOption.zip || '',
            company: opts.paymentOption.company,
        };

        fattmerchantOmniPaymentMethodID = await stax.GetPaymentToken(details);
    } else if (opts.paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
        const details = {
            addressLine1: opts.paymentOption.addressLine1 || '',
            addressLine2: opts.paymentOption.addressLine2 || '',
            city: opts.paymentOption.city || '',
            countryCode3: opts.paymentOption.country?.countryCode3 || '',
            stateCode: opts.paymentOption.state?.stateCode || '',
            bank: opts.bankAccount && opts.bankRouting && opts.accountType && opts.bankType ? {
                account: opts.bankAccount,
                routing: opts.bankRouting,
                type: opts.accountType,
                holderType: opts.bankType,
            } : undefined,
            fullName: opts.paymentOption.fullName || '',
            phone: opts.paymentOption.phone || '',
            webPaymentsToken: opts.fattmerchantOmniWebPaymentsToken || '',
            zip: opts.paymentOption.zip || '',
            company: opts.paymentOption.company,
        };
        fattmerchantOmniPaymentMethodID = await stax.GetPaymentToken(details);
    }

    let last4: number | undefined;
    if (opts.creditCardNumber) {
        last4 = parseInt(opts.creditCardNumber.slice(-4), 10);
    } else if (opts.bankAccount) {
        last4 = parseInt(opts.bankAccount.slice(-4), 10);
    }

    return axios.post<models.PaymentOption>('/payment-options', {
        'user': opts.user,
        'fullName': opts.paymentOption.fullName,
        'company': opts.paymentOption.company,
        'addressLine1': opts.paymentOption.addressLine1,
        'addressLine2': opts.paymentOption.addressLine2,
        'city': opts.paymentOption.city,
        'state': opts.paymentOption.state ? {
            'id': opts.paymentOption.state.id,
        } : undefined,
        'zip': opts.paymentOption.zip,
        'country': {
            'id': opts.paymentOption.country?.id,
        },
        'phone': opts.paymentOption.phone,
        'email': opts.paymentOption.email,

        'paymentOptionTypeID': opts.paymentOption.paymentOptionTypeID,
        'creditCardFirst6': opts.creditCardNumber ? parseInt(opts.creditCardNumber.slice(0, 6), 10) : undefined,
        'creditCardLast4': last4,
        'creditCardExpirationDate': opts.expireMonth && opts.expireYear ? `${String(opts.expireMonth).padStart(2, '0')}/${String(opts.expireYear).slice(-2).padStart(2, '0')}` : undefined,

        'accountType': opts.accountType,
        'bankType': opts.bankType,

        'fattmerchantOmniPaymentMethodID': fattmerchantOmniPaymentMethodID,
    }).then(resp => resp.data);
}

export function DeletePaymentOption(paymentOptionID: string) {
    return axios.delete(`/payment-options/${paymentOptionID}`);
}

export function RestorePaymentOption(paymentOptionID: string) {
    return axios.patch(`/payment-options/${paymentOptionID}`, { 'active': true });
}